import classNames from 'classnames';
import React, { useCallback } from 'react';

/**
 * Вид карточки — только внешний вид, поведение одно (как у Material 3: outlined / filled / elevated).
 *
 * - `outlined` — рамка `lineSubtle` на белом, по умолчанию;
 * - `plain` — белая поверхность без рамки (вкладка «Медиа», видеометки, витрина `/ui-kit`);
 * - `elevated` — белая поверхность с тенью `shadow-low` (очередь медиа, публичная часть турнира);
 * - `filled` — фон `surfacePage` без рамки: блок внутри белой модалки.
 */
export type UDCardVariant = 'outlined' | 'plain' | 'elevated' | 'filled';

/** Цвет рамки вместо `lineSubtle`: `success` — подключено, `attention` — подсказка. */
export type UDCardAccent = 'success' | 'attention';

export type UDCardRadius = 'card' | 'panel';

/** `sm` 12 px, `md` 16 px, `lg` 20 px; `none` — для обложки и `overflow-hidden`. */
export type UDCardPadding = 'none' | 'sm' | 'md' | 'lg';

export type UDCardTag = 'div' | 'section' | 'article';

export interface UDCardStyleOptions {
  variant?: UDCardVariant;
  accent?: UDCardAccent;
  radius?: UDCardRadius;
  padding?: UDCardPadding;
  isSelected?: boolean;
  /**
   * Временная подсветка «вот она» после действия (перемещённый шаблон): рамка `accentNaplesYellow`
   * и фон `accentCornsilk` с плавным переходом. Сильнее `accent` и `isSelected`.
   */
  isHighlighted?: boolean;
  /**
   * Ховер тенью. `UDCard` ставит его сам, если передан `onClick`; без `onClick` — для карточки,
   * кликабельной частью (кнопка внутри) или ссылкой целиком.
   */
  interactive?: boolean;
}

export interface UDCardProps extends UDCardStyleOptions, Omit<React.HTMLAttributes<HTMLElement>, 'onClick' | 'children'> {
  children: React.ReactNode;
  as?: UDCardTag;
  onClick?: () => void;
}

const RADIUS_CLASS: Record<UDCardRadius, string> = {
  card: 'rounded-card',
  panel: 'rounded-panel',
};

const PADDING_CLASS: Record<UDCardPadding, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-5',
};

type EdgeColor = 'subtle' | 'strong' | 'success' | 'attention';

/** Рамка `outlined`: у неё уже есть `border`, меняется только цвет. */
const BORDER_CLASS: Record<EdgeColor, string> = {
  subtle: 'border border-lineSubtle',
  strong: 'border border-lineStrong',
  success: 'border border-CaseSuccess',
  attention: 'border border-accentNaplesYellow',
};

/**
 * Обводка вариантов без рамки — кольцо внутрь (`box-shadow`), а не `border`: рамка в 1 px сдвинула бы
 * содержимое при выборе или подсветке. `inset-ring` складывается с `shadow-low` у `elevated`.
 */
const INSET_RING_CLASS: Record<Exclude<EdgeColor, 'subtle'>, string> = {
  strong: 'inset-ring inset-ring-lineStrong',
  success: 'inset-ring inset-ring-CaseSuccess',
  attention: 'inset-ring inset-ring-accentNaplesYellow',
};

/**
 * Цвет края выбирается здесь и только один: два `border-*` цвета в одном `className` решает порядок
 * правил в CSS Tailwind, а не порядок в строке. Поэтому вызывающий цвет рамки не передаёт —
 * только `accent` / `isSelected` / `isHighlighted`. Приоритет: подсветка → выбор → акцент.
 */
function edgeClass({ variant, accent, isSelected, isHighlighted }: UDCardStyleOptions): string {
  const color: Exclude<EdgeColor, 'subtle'> | null = isHighlighted ? 'attention' : isSelected ? 'strong' : (accent ?? null);

  if (variant === 'outlined') {
    return BORDER_CLASS[color ?? 'subtle'];
  }

  return color ? INSET_RING_CLASS[color] : '';
}

/**
 * Классы поверхности карточки. Экспортируется для двух случаев, где `UDCard` не рендерится сам:
 * ссылка-карточка (`UDLink className={udCardClassName(…)}`) и скелетон, который обязан совпасть
 * с живой карточкой по полям и радиусу (`.claude/docs/skeleton-kit.md`).
 */
export function udCardClassName({
  variant = 'outlined',
  accent,
  radius = 'card',
  padding = 'md',
  isSelected = false,
  isHighlighted,
  interactive = false,
}: UDCardStyleOptions = {}): string {
  return classNames(
    surfaceClass(variant, Boolean(isHighlighted)),
    variant === 'elevated' && 'shadow-low',
    edgeClass({ variant, accent, isSelected, isHighlighted }),
    RADIUS_CLASS[radius],
    PADDING_CLASS[padding],
    // Переход один на всё, что у карточки меняется: тень ховера и цвета подсветки. Подсветка гаснет
    // плавно, только если проп передан и в выключенном состоянии (`false`, а не пропущен), и дольше
    // ховера — 300 ms, верхняя граница §1 «Движение»: «вот она» глаз должен успеть заметить.
    (interactive || isHighlighted !== undefined) && 'transition ease-out motion-reduce:transition-none',
    isHighlighted !== undefined ? 'duration-300' : interactive && 'duration-150',
    interactive && 'hover:shadow-low focus-visible:shadow-low',
  );
}

function surfaceClass(variant: UDCardVariant, isHighlighted: boolean): string {
  if (isHighlighted) {
    return 'bg-accentCornsilk';
  }

  return variant === 'filled' ? 'bg-surfacePage' : 'bg-surfaceCard';
}

/**
 * Карточка — поверхность, на которой лежит блок экрана.
 *
 * `className` — только раскладка содержимого (`flex`, `gap`, `max-w-*`); цвет фона, рамки, радиус
 * и поля задаются пропсами. С `onClick` карточка становится кнопкой: `role="button"`, фокус с
 * клавиатуры, Enter и Space (WAI-ARIA APG, Button pattern). Атрибуты (`data-testid`, `aria-*`)
 * уходят на корень.
 */
const UDCard = React.forwardRef<HTMLElement, UDCardProps>(function UDCard(
  {
    as: Tag = 'div',
    variant,
    accent,
    radius,
    padding,
    isSelected,
    isHighlighted,
    interactive,
    onClick,
    className,
    children,
    onKeyDown,
    ...rest
  },
  ref,
) {
  const isInteractive = Boolean(onClick) || Boolean(interactive);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      onKeyDown?.(event);

      if (!onClick || event.defaultPrevented || event.target !== event.currentTarget) {
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        // Space прокручивал бы страницу.
        event.preventDefault();
        onClick();
      }
    },
    [onClick, onKeyDown],
  );

  return (
    <Tag
      {...rest}
      // Корень — один из трёх тегов; ref у всех HTMLElement, а union-тег TS не сводит.
      ref={ref as React.Ref<HTMLDivElement>}
      {...(onClick ? { role: 'button', tabIndex: 0, onClick, onKeyDown: handleKeyDown } : { onKeyDown })}
      aria-pressed={onClick && isSelected !== undefined ? isSelected : undefined}
      className={classNames(
        udCardClassName({ variant, accent, radius, padding, isSelected, isHighlighted, interactive: isInteractive }),
        onClick && 'cursor-pointer',
        className,
      )}
    >
      {children}
    </Tag>
  );
});

export default UDCard;
